import {
  faAnglesLeft,
  faAnglesRight,
  faArrowLeft,
  faArrowRight,
  faPencil,
  faRotateLeft,
  faRotateRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useKeycloak } from "@react-keycloak/web";
import React, { type FC } from "react";
import { useApiCall } from "../../api/api";
import type { InspectionPhoto } from "../../api/dto/InspectionPhoto";
import type { Question } from "../../api/dto/Question";
import { DeletePhotoModal } from "../../modals/DeletePhotoModal";
import { EditPhotoDescription } from "../../modals/EditPhotoDescription";
import { Button, IconButton } from "../form/Button";
import { useModal } from "../layout/ModalProvider";
import { useAssetUrl } from "./useAssetUrl";
export const InspectionPhotoOverviewCard: FC<{
  photo: InspectionPhoto;
  questions?: Question[];
  prev?: () => void;
  next?: () => void;
  toFront?: () => void;
  toBack?: () => void;
  update: (photo: Pick<InspectionPhoto, "rotation" | "description" | "questionId">) => void;
  delete: () => void;
  reload: () => Promise<void>;
}> = (props) => {
  const { updatePhoto } = useApiCall();
  const { getInspectionPhotoUrl } = useAssetUrl();
  const result = getInspectionPhotoUrl(props.photo);
  const linkedQuestion = props.questions?.find((q) => q.id === props.photo.questionId);
  const photoDescriptionModal = useModal({
    title: "Toelichting",
    body: (
      <EditPhotoDescription
        photo={props.photo}
        questions={props.questions}
        onFinished={(p) => props.update(p)}
      />
    ),
  });
  const photoDeleteModal = useModal({
    title: "Verwijderen",
    body: <DeletePhotoModal photo={props.photo} onFinished={() => props.delete()} />,
  });
  const rotate = async (delta: number) => {
    if (props.photo.rotation <= 0 && delta < 0) {
      await updatePhoto(
        props.photo.inspectionId,
        props.photo.id,
        360 + delta,
        props.photo.description,
      );
      props.update({
        description: props.photo.description,
        rotation: 360 + delta,
        questionId: props.photo.questionId,
      });
    } else {
      await updatePhoto(
        props.photo.inspectionId,
        props.photo.id,
        (props.photo.rotation + delta) % 360,
        props.photo.description,
      );
      props.update({
        description: props.photo.description,
        rotation: (props.photo.rotation + delta) % 360,
        questionId: props.photo.questionId,
      });
    }
  };
  return (
    <div
      className={
        "mr-4 mb-4 border border-slate-300 rounded-xl px-2 py-2 flex flex-col items-stretch"
      }
    >
      <div className={"relative"}>
        <a href={result + "&quality=original"} target={"_blank"} rel="noopener">
          <div className={"absolute inset-0 overflow-hidden rounded-xl"}>
            <img
              src={result}
              alt={""}
              className={"blur h-72 w-72 object-cover rounded-lg"}
              style={{ transform: `rotate(${props.photo.rotation}deg)` }}
            />
          </div>
          <img
            src={result}
            alt={""}
            className={"h-72 w-72 object-contain bg-[#fff5] rounded-lg"}
            style={{ transform: `rotate(${props.photo.rotation}deg)` }}
          />
        </a>
        <div
          className={
            "h-5 w-5 absolute top-2 left-2 border border-white rounded-full bg-[#0008] flex items-center justify-center text-xs text-white"
          }
        >
          {props.photo.order}
        </div>
        {linkedQuestion && (
          <div
            className={
              "absolute top-2 right-2 border border-white rounded-full bg-[#0008] px-2 h-5 flex items-center justify-center text-xs text-white"
            }
          >
            Vraag {linkedQuestion.number}
          </div>
        )}
      </div>
      <div className={"flex items-center justify-center"}>
        <IconButton
          icon={faAnglesLeft}
          type={"secondary"}
          size={"xs"}
          disabled={!props.toFront}
          onClick={() => props.toFront?.()}
        />
        <IconButton
          icon={faArrowLeft}
          type={"secondary"}
          size={"xs"}
          disabled={!props.prev}
          onClick={() => props.prev?.()}
        />
        <div className={"flex-1 flex items-center justify-center"}>
          <IconButton
            icon={faRotateLeft}
            type={"secondary"}
            size={"xs"}
            onClick={() => rotate(-90)}
          />
          <IconButton
            icon={faRotateRight}
            type={"secondary"}
            size={"xs"}
            onClick={() => rotate(90)}
          />
        </div>
        <IconButton
          icon={faArrowRight}
          type={"secondary"}
          size={"xs"}
          disabled={!props.next}
          onClick={() => props.next?.()}
        />
        <IconButton
          icon={faAnglesRight}
          type={"secondary"}
          size={"xs"}
          disabled={!props.toBack}
          onClick={() => props.toBack?.()}
        />
      </div>
      <div className={"text-sm my-2 w-64"}>{props.photo.description}</div>
      <div className={"flex items-center justify-between"}>
        <Button
          type={"secondary"}
          size={"xs"}
          icon={faPencil}
          text={"Bewerken"}
          onClick={() => photoDescriptionModal.open()}
        />
        <Button
          type={"danger"}
          size={"xs"}
          icon={faTrash}
          text={"Verwijderen"}
          onClick={() => photoDeleteModal.open()}
        />
      </div>
    </div>
  );
};
